import { useEffect, useRef, useState } from 'react';
import useDocumentTitle from '../../../../hooks/useDocumentTitle';
import { GlobalService } from '../../../../services/GlobalService';
import { UserService } from '../../../../services/UserService';
import { STATUS_SUCCESS } from '../../../../utils/constants';
import { GetBusinessType, GetCustomerGroup } from '../../../../utils/HelperFunctions';
import { useLoader } from '../../../../context/LoaderContext';
import { ToasterService } from '../../../../services/ToasterService';
import config from '../../../../config/environment';
import { useNavigate } from 'react-router-dom';

type FormData = {
  firstName: string;
  phoneNumber: string;
  shopName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  openingTimeFrom: string;
  openingTimeTo: string;
  deliveryTimeFrom: string;
  deliveryTimeTo: string;
  businessType: string;
  customerGroup: string;
  profileImage: any;
};

type Errors = Partial<Record<keyof FormData, string>>;

export default function UseUserProfilePage() {
  const SERVER_URL = config.SERVER_URL;
  useDocumentTitle('Namasteji Manikchand - Address');
  const [errors, setErrors] = useState<Errors>({});
  const [stateList, setStateList] = useState<string[]>([]);
  const [countryList, setCountryList] = useState<{ name: string }[]>([]);
  const newErrors: Errors = {};
  const [profileImageVal, setProfileImageVal] = useState<string>('');
  const [businessTypeOptions, setBusinessTypes] = useState<any>([]);
  const [customerGroupOptions, setCustomerGroup] = useState<any>([]);
  const [selectedBusinessType, setSelectedBusinessType] = useState<any>(null);
  const [selectedCustomerGroup, setSelectedCustomerGroup] = useState<any>(null);
  let parsedUserData: any = {};
  const getUserLoginData = localStorage.getItem('userLoginData');
  const { setLoading } = useLoader();
  const navigate = useNavigate();

  useEffect(() => {
    if (getUserLoginData) {
      parsedUserData = JSON.parse(getUserLoginData);
      if (parsedUserData && parsedUserData.customer_id) {
        (async () => {
          const getBusinessTypes = await GetBusinessType();
          setBusinessTypes(getBusinessTypes);

          const getCustomerGroup = await GetCustomerGroup();
          setCustomerGroup(getCustomerGroup);
        })();
        getProfile();
        getState();
        getCountry();
      } else {
        navigate('/login');
      }
    } else {
      navigate('/login');
    }
  }, []);

  const [formData, setFormData] = useState({
    customer_id: parsedUserData.customer_id,
    firstName: '',
    phoneNumber: '',
    shopName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    openingTimeFrom: '',
    openingTimeTo: '',
    deliveryTimeFrom: '',
    deliveryTimeTo: '',
    businessType: '',
    customerGroup: '',
    profileImage: null as File | null,
  });

  async function getProfile() {
    try {
      setLoading(true);
      const resp = await UserService.Profile(parsedUserData.customer_id);
      const profileData = resp.message;
      if (profileData.status === STATUS_SUCCESS) {
        setLoading(false);
        if (profileData && profileData.profile && profileData.profile !== '') {
          const imgFullUrl = SERVER_URL + profileData.profile;
          setProfileImageVal(imgFullUrl);
        }
        if (profileData && profileData.industry && profileData.industry !== '') {
          const setIndusryValue = {
            value: profileData.industry,
            label: profileData.industry,
          };
          setSelectedBusinessType(setIndusryValue);
        }

        if (profileData && profileData.customer_group && profileData.customer_group !== '') {
          const setCustomerGroupValue = {
            value: profileData.customer_group,
            label: profileData.customer_group,
          };
          setSelectedCustomerGroup(setCustomerGroupValue);
        }

        setFormData({
          customer_id: parsedUserData.customer_id,
          firstName: profileData.full_name ? profileData.full_name : '',
          phoneNumber: profileData.mobile ? profileData.mobile : '',
          shopName: profileData.shop_name ? profileData.shop_name : '',
          address1: profileData.customer_address.address_line1 ? profileData.customer_address.address_line1 : '',
          address2: profileData.customer_address.address_line2 ? profileData.customer_address.address_line2 : '',
          city: profileData.customer_address.city ? profileData.customer_address.city : '',
          state: profileData.customer_address.state ? profileData.customer_address.state : '',
          country: profileData.customer_address.country ? profileData.customer_address.country : '',
          pincode: profileData.customer_address.pincode ? profileData.customer_address.pincode : '',
          openingTimeFrom: profileData.start_time ? formatTimeForInput(profileData.start_time) : '',
          openingTimeTo: profileData.end_time ? formatTimeForInput(profileData.end_time) : '',
          deliveryTimeFrom: profileData.from_time ? formatTimeForInput(profileData.from_time) : '',
          deliveryTimeTo: profileData.to_time ? formatTimeForInput(profileData.to_time) : '',
          businessType: profileData.industry ? profileData.industry : '',
          customerGroup: profileData.customer_group ? profileData.customer_group : '',
          profileImage: null,
        });
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error fetching profile:', error);
    }
  }

  const formatTimeForInput = (timeStr: string) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const paddedHour = h.padStart(2, '0');
    return `${paddedHour}:${m}`;
  };
  const getState = async () => {
    const resp = await GlobalService.State();
    setStateList(resp.message);
  };

  const getCountry = async () => {
    const resp = await GlobalService.Country();
    setCountryList(resp.message);
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChangeBusineType = (selectedOption: any) => {
    setSelectedBusinessType(selectedOption);
    setFormData(prev => ({
      ...prev,
      businessType: selectedOption ? selectedOption.value : '',
    }));
    setErrors(prev => ({ ...prev, businessType: '' }));
  };

  const handleCustomerGroup = (selectedOption: any) => {
    setSelectedCustomerGroup(selectedOption);
    setFormData(prev => ({
      ...prev,
      customerGroup: selectedOption ? selectedOption.value : '',
    }));
    setErrors(prev => ({ ...prev, customerGroup: '' }));
  };

  const validate = (): boolean => {
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required';
    }

    const phoneNumber = formData.phoneNumber.trim();
    if (!phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\d+$/.test(phoneNumber)) {
      newErrors.phoneNumber = 'Mobile number must contain only digits';
    } else if (phoneNumber.length !== 10) {
      newErrors.phoneNumber = 'Mobile number must be exactly 10 digits';
    } else if (!/^[6-9]\d{9}$/.test(phoneNumber)) {
      newErrors.phoneNumber = 'Enter a valid 10-digit Indian mobile number';
    }

    if (!formData.shopName.trim()) {
      newErrors.shopName = 'Shop Name is required';
    }
    if (!formData.address1.trim()) {
      newErrors.address1 = 'Address-1 is required';
    }
    if (!formData.address2.trim()) {
      newErrors.address2 = 'Address-2 is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state.trim()) {
      newErrors.state = 'Please select State';
    }
    if (!formData.country.trim()) {
      newErrors.country = 'Please select Country';
    }

    const pincode = formData.pincode.trim();
    if (!pincode) {
      newErrors.pincode = 'Pinocode is required';
    } else if (pincode.length !== 6) {
      newErrors.pincode = 'Pincode Must be exactly 10 digits';
    }

    if (!formData.openingTimeFrom.trim()) {
      newErrors.openingTimeFrom = 'Opening Time is required';
    }
    if (!formData.openingTimeTo.trim()) {
      newErrors.openingTimeTo = 'Closeing time is required.';
    }
    if (!formData.deliveryTimeFrom.trim()) {
      newErrors.deliveryTimeFrom = 'Delivery From Time is required.';
    }
    if (!formData.deliveryTimeTo.trim()) {
      newErrors.deliveryTimeTo = 'Delivery To time is required.';
    }
    if (!formData.businessType.trim()) {
      newErrors.businessType = 'Business type is required';
    }
    if (!formData.customerGroup.trim()) {
      newErrors.customerGroup = 'Customer Group is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      const imageUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        profileImage: file,
      }));
      setProfileImageVal(imageUrl);
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      // const payload = {
      //     "customer_id": formData.customer_id,
      //     "customer_name": formData.firstName,
      //     "new_mobile": formData.phoneNumber,
      //     "shop_name": formData.shopName,
      //     "custom_start_time": formData.openingTimeFrom + ":00",
      //     "custom_end_time": formData.openingTimeTo + ":00",
      //     "custom_from": formData.deliveryTimeFrom + ":00",
      //     "custom_to": formData.deliveryTimeTo + ":00",
      //     "industry": formData.businessType,
      //     "file": formData.profileImage,
      //     "customer_address": {
      //         "address_line1": formData.address1,
      //         "address_line2": formData.address2,
      //         "state": formData.state,
      //         "country": formData.country,
      //         "pincode": formData.pincode,
      //         "city": formData.city
      //     }
      // };
      //  const response = await UserService.UpdateProfileApiClient(payload);

      /**  Append all fields  */
      const formDataObj = new FormData();
      formDataObj.append('customer_id', formData.customer_id);
      formDataObj.append('customer_name', formData.firstName);
      formDataObj.append('new_mobile', formData.phoneNumber);
      formDataObj.append('shop_name', formData.shopName);
      formDataObj.append('custom_start_time', formData.openingTimeFrom + ':00');
      formDataObj.append('custom_end_time', formData.openingTimeTo + ':00');
      formDataObj.append('custom_from', formData.deliveryTimeFrom + ':00');
      formDataObj.append('custom_to', formData.deliveryTimeTo + ':00');
      formDataObj.append('industry', formData.businessType);
      formDataObj.append('customer_group', formData.customerGroup);
      if (formData.profileImage) {
        formDataObj.append('file', formData.profileImage);
      }
      formDataObj.append('customer_address[address_line1]', formData.address1);
      formDataObj.append('customer_address[address_line2]', formData.address2 || '');
      formDataObj.append('customer_address[state]', formData.state);
      formDataObj.append('customer_address[country]', formData.country);
      formDataObj.append('customer_address[pincode]', formData.pincode);
      formDataObj.append('customer_address[city]', formData.city);

      const response = await UserService.UpdateProfile(formDataObj);
      const apiResp = response.message;
      // console.log('apiResp', response);
      if (apiResp.status === STATUS_SUCCESS) {
        // getProfile();
        ToasterService.Success('Profile Updated Successfully!');
        setLoading(false);
        // console.log("Response from response UpdateProfileApiClient:", apiResp);
      } else {
        setLoading(false);
        ToasterService.Error('Something wnet wroung. Please try again after sometime!');
      }
    } catch (err) {
      console.log('err', err);
      setLoading(false);
      ToasterService.Error('Server Error. Please try agin after sometime!');
      // console.log('ErrorCatch', err)
    }
  };

  const states = {
    errors,
    formData,
    stateList,
    countryList,
    profileImageVal,
    businessTypeOptions,
    selectedBusinessType,
    selectedCustomerGroup,
    customerGroupOptions,
  };

  return {
    handleChange,
    handleSubmit,
    fileInputRef,
    handleFileChange,
    handleButtonClick,
    states,
    handleChangeBusineType,
    handleCustomerGroup,
    navigate,
  };
}
